import React, { useEffect, useState } from "react";
import { LowCreditBanner } from "@/components/CreditDialog";
import { WeeklySummaryCard } from "@/components/WeeklySummaryCard";
import { useAuth } from "@/context/AuthContext";
import { api, fmt, CURRENCIES } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
    Users,
    Stethoscope,
    TrendingUp,
    TrendingDown,
    Wallet,
    PiggyBank,
    Instagram,
    AlertTriangle,
    CalendarClock,
} from "lucide-react";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
} from "recharts";

const MONTHS_TR = [
    "Oca",
    "Şub",
    "Mar",
    "Nis",
    "May",
    "Haz",
    "Tem",
    "Ağu",
    "Eyl",
    "Eki",
    "Kas",
    "Ara",
];

function trPretty(n) {
    return Number(n || 0).toLocaleString("tr-TR", {
        maximumFractionDigits: 0,
    });
}

function LicenseBanner({ daysRemaining, licenseUntil }) {
    const isCritical = daysRemaining <= 7;
    const isExpired = daysRemaining < 0;
    const style = isExpired
        ? {
              wrap: "bg-gradient-to-r from-rose-50 to-rose-100 border-rose-300",
              iconBg: "bg-rose-500",
              title: "text-rose-900",
              body: "text-rose-800",
          }
        : isCritical
          ? {
                wrap: "bg-gradient-to-r from-amber-50 to-orange-100 border-amber-300",
                iconBg: "bg-amber-500",
                title: "text-amber-900",
                body: "text-amber-800",
            }
          : {
                wrap: "bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200",
                iconBg: "bg-teal-500",
                title: "text-teal-900",
                body: "text-teal-800",
            };
    const title = isExpired
        ? "Lisans süresi doldu"
        : isCritical
          ? `Lisansınızın bitmesine ${daysRemaining} gün kaldı!`
          : `Lisansınızın bitmesine ${daysRemaining} gün kaldı`;
    const desc = isExpired
        ? "Panele erişiminiz kısıtlanmış olabilir. Yenilemek için hemen iletişime geçin."
        : `Bitiş tarihi: ${licenseUntil?.slice(0, 10)}. Kesintisiz kullanım için yenileyin.`;

    return (
        <div
            className={`rounded-2xl border ${style.wrap} p-4 mb-6 flex items-center justify-between gap-4 flex-wrap`}
            data-testid="license-banner"
        >
            <div className="flex items-center gap-3">
                <div
                    className={`w-11 h-11 rounded-xl ${style.iconBg} flex items-center justify-center text-white shadow-lg`}
                >
                    {isCritical || isExpired ? (
                        <AlertTriangle className="w-5 h-5" />
                    ) : (
                        <CalendarClock className="w-5 h-5" />
                    )}
                </div>
                <div>
                    <div className={`font-semibold ${style.title}`}>
                        {title}
                    </div>
                    <div className={`text-sm ${style.body} mt-0.5`}>{desc}</div>
                </div>
            </div>
            <a
                href="https://www.instagram.com/luxbeamyazilim"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="renew-license-instagram-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white text-sm font-semibold shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all whitespace-nowrap"
            >
                <Instagram className="w-4 h-4" />
                Yenilemek İçin İletişime Geç
            </a>
        </div>
    );
}

function DonutBlock({ title, data, color, total, subtitle }) {
    return (
        <div className="relative rounded-2xl p-6 bg-white border border-slate-200 shadow-sm overflow-hidden">
            <div
                className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-20"
                style={{
                    background: `radial-gradient(circle, ${color} 0%, transparent 60%)`,
                }}
            />
            <div className="flex items-center justify-between mb-3 relative">
                <h3 className="font-semibold text-slate-800">{title}</h3>
                <span
                    className="text-xs font-medium uppercase tracking-widest"
                    style={{ color }}
                >
                    {subtitle}
                </span>
            </div>
            <div className="relative h-56">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <defs>
                            {data.map((_, i) => (
                                <linearGradient
                                    key={i}
                                    id={`grad-${title}-${i}`}
                                    x1="0"
                                    y1="0"
                                    x2="1"
                                    y2="1"
                                >
                                    <stop
                                        offset="0%"
                                        stopColor={data[i].fill}
                                        stopOpacity={1}
                                    />
                                    <stop
                                        offset="100%"
                                        stopColor={data[i].fill}
                                        stopOpacity={0.55}
                                    />
                                </linearGradient>
                            ))}
                            <filter
                                id={`shadow-${title}`}
                                x="-30%"
                                y="-30%"
                                width="160%"
                                height="160%"
                            >
                                <feDropShadow
                                    dx="0"
                                    dy="6"
                                    stdDeviation="6"
                                    floodColor={color}
                                    floodOpacity="0.35"
                                />
                            </filter>
                        </defs>
                        <Pie
                            data={data}
                            innerRadius={62}
                            outerRadius={90}
                            paddingAngle={3}
                            dataKey="value"
                            filter={`url(#shadow-${title})`}
                            stroke="none"
                            animationDuration={900}
                        >
                            {data.map((_, i) => (
                                <Cell
                                    key={i}
                                    fill={`url(#grad-${title}-${i})`}
                                />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(v, _n, o) =>
                                `${fmt(v, o.payload.currency)}`
                            }
                        />
                    </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-xs uppercase tracking-widest text-slate-500">
                        Toplam TL
                    </span>
                    <span
                        className="text-2xl font-extrabold mono"
                        style={{ color }}
                    >
                        ₺{trPretty(total)}
                    </span>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
                {data.map((d) => (
                    <div
                        key={d.currency}
                        className="flex items-center gap-2 text-sm"
                    >
                        <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ background: d.fill }}
                        />
                        <span className="text-slate-600">{d.currency}</span>
                        <span className="ml-auto font-semibold mono text-slate-800">
                            {fmt(d.raw, d.currency)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function Overview() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [report, setReport] = useState(null);

    useEffect(() => {
        api.get("/clinic/dashboard").then((r) => setData(r.data));
        api.get("/clinic/monthly-report").then((r) => setReport(r.data));
    }, []);

    if (!data)
        return (
            <div data-testid="overview-loading" className="text-slate-500">
                Yükleniyor...
            </div>
        );

    const revColors = ["#00A896", "#0F4C5C", "#3B82F6", "#8B5CF6"];
    const expColors = ["#EF4444", "#F59E0B", "#EC4899", "#F97316"];

    const revData = CURRENCIES.map((c, i) => ({
        currency: c.code,
        raw: data.revenue[c.code] || 0,
        value: Math.max(0.001, data.revenue[c.code] || 0),
        fill: revColors[i],
    }));
    const expData = CURRENCIES.map((c, i) => ({
        currency: c.code,
        raw: data.expenses[c.code] || 0,
        value: Math.max(0.001, data.expenses[c.code] || 0),
        fill: expColors[i],
    }));

    const revTL = data.revenue.TRY || 0;
    const expTL = data.expenses.TRY || 0;
    const monthly = (report?.months || []).map((m) => {
        const [_, mm] = m.key.split("-");
        return {
            name: MONTHS_TR[parseInt(mm, 10) - 1],
            key: m.key,
            gelir: m.revenue_try,
            gider: m.expenses_try,
            kar: m.profit_try,
        };
    });
    const yearProfit = monthly.reduce((a, b) => a + b.kar, 0);
    const currentMonth = monthly[monthly.length - 1];

    return (
        <div data-testid="overview-page">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-slate-900">
                    {data.clinic_name}
                </h1>
                <p className="text-slate-600 mt-1">Genel Bakış</p>
            </div>

            {data.license_until && data.days_remaining !== null && data.days_remaining <= 30 && (
                <LicenseBanner
                    daysRemaining={data.days_remaining}
                    licenseUntil={data.license_until}
                />
            )}

            <LowCreditBanner data={data} />

            {user?.role === "patron" && <WeeklySummaryCard />}

            <div className="grid md:grid-cols-4 gap-4 mb-8">
                <div className="dk-stat-card">
                    <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                            Hasta
                        </span>
                        <Users className="w-4 h-4 text-teal-600" />
                    </div>
                    <div
                        className="text-3xl font-bold text-slate-900 mt-2"
                        data-testid="stat-patients"
                    >
                        {data.patient_count}
                    </div>
                </div>
                <div className="dk-stat-card">
                    <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                            Tedavi
                        </span>
                        <Stethoscope className="w-4 h-4 text-teal-600" />
                    </div>
                    <div
                        className="text-3xl font-bold text-slate-900 mt-2"
                        data-testid="stat-treatments"
                    >
                        {data.treatment_count}
                    </div>
                </div>
                <div className="dk-stat-card">
                    <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                            Bu Ay Kâr (TL)
                        </span>
                        <PiggyBank className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div
                        className={`text-3xl font-bold mt-2 mono ${(currentMonth?.kar || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                        data-testid="stat-month-profit"
                    >
                        ₺{trPretty(currentMonth?.kar || 0)}
                    </div>
                </div>
                <div className="dk-stat-card">
                    <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                            Yıllık Kâr (TL)
                        </span>
                        <Wallet className="w-4 h-4 text-amber-600" />
                    </div>
                    <div
                        className={`text-3xl font-bold mt-2 mono ${yearProfit >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                    >
                        ₺{trPretty(yearProfit)}
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
                <DonutBlock
                    title="Gelir Dağılımı"
                    subtitle="Ödemeler"
                    color="#00A896"
                    total={revTL}
                    data={revData}
                />
                <DonutBlock
                    title="Gider Dağılımı"
                    subtitle="Harcamalar"
                    color="#EF4444"
                    total={expTL}
                    data={expData}
                />
            </div>

            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-xl font-semibold text-slate-900">
                                Aylık Kâr / Zarar Analizi (TL)
                            </h3>
                            <p className="text-sm text-slate-500 mt-1">
                                Son 12 ay — anlaşma kurları ile TL'ye çevrilmiş
                            </p>
                        </div>
                        <div className="flex gap-4 text-xs">
                            <span className="flex items-center gap-1.5 text-emerald-700">
                                <TrendingUp className="w-3.5 h-3.5" />
                                Gelir
                            </span>
                            <span className="flex items-center gap-1.5 text-rose-700">
                                <TrendingDown className="w-3.5 h-3.5" />
                                Gider
                            </span>
                        </div>
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={monthly}
                                margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
                            >
                                <defs>
                                    <linearGradient
                                        id="gelirG"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor="#00A896"
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor="#0F4C5C"
                                        />
                                    </linearGradient>
                                    <linearGradient
                                        id="giderG"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor="#F87171"
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor="#B91C1C"
                                        />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#E2E8F0"
                                />
                                <XAxis
                                    dataKey="name"
                                    stroke="#64748B"
                                    fontSize={12}
                                />
                                <YAxis
                                    stroke="#64748B"
                                    fontSize={12}
                                    tickFormatter={(v) =>
                                        `₺${trPretty(v)}`
                                    }
                                />
                                <Tooltip
                                    formatter={(v) => `₺${trPretty(v)}`}
                                    contentStyle={{
                                        border: "1px solid #E2E8F0",
                                        borderRadius: 8,
                                    }}
                                />
                                <Legend />
                                <Bar
                                    dataKey="gelir"
                                    name="Gelir"
                                    fill="url(#gelirG)"
                                    radius={[6, 6, 0, 0]}
                                />
                                <Bar
                                    dataKey="gider"
                                    name="Gider"
                                    fill="url(#giderG)"
                                    radius={[6, 6, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
